import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { initOneSignal, onesignalLogin, onesignalLogout } from "@/lib/onesignal";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("fidelis_user") || "null"); } catch { return null; }
  });
  const [loading, setLoading] = useState(false);

  const login = useCallback(async (username, password) => {
    const { data } = await api.post("/auth/login", { username, password });
    localStorage.setItem("fidelis_token", data.access_token);
    localStorage.setItem("fidelis_user", JSON.stringify(data.user));
    setUser(data.user);
    // Initialize OneSignal then explicitly link this user (External ID)
    await initOneSignal();
    onesignalLogin(data.user.id);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    onesignalLogout();
    localStorage.removeItem("fidelis_token");
    localStorage.removeItem("fidelis_user");
    setUser(null);
    window.location.href = "/login";
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem("fidelis_user", JSON.stringify(data));
      // Re-link OneSignal on every refresh (covers app reopens with valid token)
      onesignalLogin(data.id);
      return data;
    } catch (e) {
      return null;
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem("fidelis_token") && !user) {
      setLoading(true);
      refresh().finally(() => setLoading(false));
    } else if (user?.id) {
      // User already in localStorage on app boot → re-link to OneSignal
      onesignalLogin(user.id);
    }
  }, [refresh, user]);

  return (
    <AuthCtx.Provider value={{ user, login, logout, refresh, loading }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
